import { useEffect, useState } from 'react';
import { Mail, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { reconciliationApi } from '@/api/reconciliation';
import { api } from '@/api/client';
import { buildReconciliationEmailDraft, type LegalEntity } from '@shared/types';

interface EmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: number;
  clientName: string;
  legalEntity: LegalEntity;
  dateFrom: string;
  dateTo: string;
  signerId?: string;
}

export function EmailDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  legalEntity,
  dateFrom,
  dateTo,
  signerId,
}: EmailDialogProps) {
  const draft = buildReconciliationEmailDraft({ legalEntity, dateFrom, dateTo });
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject(draft.subject);
      setBody(draft.body);
    }
  }, [open, draft.subject, draft.body]);

  const handleSend = async () => {
    if (!email.trim()) {
      toast.error('Введите email получателя');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error('Некорректный email адрес');
      return;
    }

    setIsSending(true);
    try {
      // Get PDF as base64
      const pdfBase64 = await reconciliationApi.getPdfAsBase64({
        clientId,
        legalEntity,
        dateFrom,
        dateTo,
        signerId,
      });

      // Send email
      await api.post('/email/send', {
        to: email.trim(),
        subject,
        body: body.replace(/\n/g, '<br>'),
        attachmentBase64: pdfBase64,
        attachmentName: `Акт_сверки_${clientName}_${dateFrom}_${dateTo}.pdf`,
      });

      toast.success('Email отправлен');
      onOpenChange(false);
    } catch (error: unknown) {
      console.error('Error sending email:', error);
      const errorMessage = error instanceof Error
        ? error.message
        : (error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Ошибка отправки';
      toast.error('Ошибка отправки email', { description: errorMessage });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Отправить акт сверки
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email получателя *</Label>
            <Input
              id="email"
              type="email"
              placeholder="example@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Тема письма</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">Текст письма</Label>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
            />
          </div>

          <div className="text-sm text-muted-foreground bg-muted p-3 rounded">
            <div className="font-medium mb-1">Вложение:</div>
            <div>Акт_сверки_{clientName}_{dateFrom}_{dateTo}.pdf</div>
          </div>
        </div>

        <div className="flex-shrink-0 pt-4 border-t space-y-2">
          <Button
            className="w-full"
            onClick={handleSend}
            disabled={isSending || !email.trim()}
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Отправка...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Отправить
              </>
            )}
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => onOpenChange(false)}
            disabled={isSending}
          >
            Отмена
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
